import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";

export default function FederalFOIA() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Navigation />

      <div className="w-full bg-brand-martinique">
        <div className="max-w-[1440px] mx-auto px-8 pt-4">
          <Breadcrumbs
            items={[
              { label: "Wiki", href: "/wiki" },
              { label: "Federal FOIA Law" },
            ]}
            variant="dark"
          />
        </div>

        <section className="w-full bg-brand-martinique py-12 lg:py-16 border-b border-black/10">
          <div className="max-w-[1440px] mx-auto px-8">
            <h1 className="font-agency text-h1 font-bold text-white mb-2">
              Federal FOIA Law
            </h1>
            <p className="font-martianmono text-text-lg-custom text-brand-gray mb-8">
              Complete guide to the Freedom of Information Act (FOIA)
            </p>

            <div className="grid lg:grid-cols-4 gap-6">
              <div className="bg-brand-dark rounded-lg p-6 space-y-2 border border-brand-orange/20">
                <p className="text-brand-orange font-martianmono text-xs font-bold uppercase">
                  Response Time
                </p>
                <p className="text-white font-martianmono text-lg font-bold">
                  20 business days
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-2 border border-brand-orange/20">
                <p className="text-brand-orange font-martianmono text-xs font-bold uppercase">
                  Exemptions
                </p>
                <p className="text-white font-martianmono text-lg font-bold">
                  9 exemptions
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-2 border border-brand-orange/20">
                <p className="text-brand-orange font-martianmono text-xs font-bold uppercase">
                  Success Rate
                </p>
                <p className="text-white font-martianmono text-lg font-bold">
                  68%
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 flex items-center justify-center border border-brand-orange/20">
                <a
                  href="/foia-generator"
                  className="px-4 py-2 bg-brand-azure text-white font-martianmono font-bold rounded hover:bg-brand-azure/90 transition-colors text-center"
                >
                  Generate Request
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique py-12 lg:py-16 border-b border-black/10">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-8">
              FOIA Law Overview
            </h2>

            <div className="space-y-6">
              <div className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20">
                <h3 className="font-agency text-h4 font-bold text-white">
                  What is FOIA?
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray">
                  The Freedom of Information Act (FOIA), enacted in 1966, is the
                  cornerstone of American transparency. It establishes your
                  legal right to access records held by federal agencies. FOIA
                  applies to all executive branch agencies and requires them to
                  disclose records unless they fall under specific exemptions.
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20">
                <h3 className="font-agency text-h4 font-bold text-white">
                  Response Time Requirements
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray mb-4">
                  Federal agencies have{" "}
                  <span className="text-brand-orange font-bold">
                    20 business days
                  </span>{" "}
                  to respond to your FOIA request. This deadline is strictly
                  enforced. Agencies may request a single 10-day extension in
                  unusual circumstances, but any longer delays must be
                  justified.
                </p>
                <div className="bg-brand-martinique/30 rounded p-4 border border-brand-orange/20">
                  <p className="font-martianmono text-sm text-brand-gray">
                    <span className="text-brand-orange font-bold">
                      Pro Tip:
                    </span>{" "}
                    Keep track of your deadline. If an agency misses the
                    response date without a valid extension, you may have
                    grounds for an appeal or lawsuit.
                  </p>
                </div>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20">
                <h3 className="font-agency text-h4 font-bold text-white">
                  FOIA Exemptions
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray mb-4">
                  FOIA allows federal agencies to withhold records in 9
                  categories. However, exemptions are narrowly
                  construed—agencies must prove why records qualify for each
                  exemption:
                </p>
                <ul className="space-y-2 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                  <li>
                    <span className="font-bold">Exemption 1:</span> National
                    defense & foreign policy
                  </li>
                  <li>
                    <span className="font-bold">Exemption 2:</span> Internal
                    agency rules
                  </li>
                  <li>
                    <span className="font-bold">Exemption 3:</span> Information
                    protected by statute
                  </li>
                  <li>
                    <span className="font-bold">Exemption 4:</span> Business
                    confidential information
                  </li>
                  <li>
                    <span className="font-bold">Exemption 5:</span> Privileged
                    communications
                  </li>
                  <li>
                    <span className="font-bold">Exemption 6:</span> Privacy
                    (individuals)
                  </li>
                  <li>
                    <span className="font-bold">Exemption 7:</span> Law
                    enforcement investigations
                  </li>
                  <li>
                    <span className="font-bold">Exemption 8:</span> Financial
                    institutions
                  </li>
                  <li>
                    <span className="font-bold">Exemption 9:</span> Geological
                    information
                  </li>
                </ul>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20">
                <h3 className="font-agency text-h4 font-bold text-white">
                  Appeal Rights
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray mb-4">
                  If a federal agency denies your FOIA request, you have
                  important appeal rights:
                </p>
                <ul className="space-y-2 pl-6 font-martianmono text-text-md-custom text-brand-gray list-disc">
                  <li>
                    Request an administrative appeal within 90 days of denial
                  </li>
                  <li>
                    Seek judicial review in federal court after administrative
                    appeal
                  </li>
                  <li>Recover attorney's fees if you win in court</li>
                  <li>
                    Request expedited review if you can demonstrate compelling
                    need
                  </li>
                </ul>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20">
                <h3 className="font-agency text-h4 font-bold text-white">
                  Success Rate
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray mb-4">
                  Federal agencies disclose records in approximately 68% of
                  requests:
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 bg-brand-martinique rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-orange"
                      style={{ width: "68%" }}
                    />
                  </div>
                  <span className="text-white font-martianmono text-lg font-bold">
                    68%
                  </span>
                </div>
                <p className="font-martianmono text-sm text-brand-gray mt-4">
                  This increases significantly with well-crafted, specific
                  requests that follow proper procedure.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-8">
              Next Steps
            </h2>

            <div className="grid lg:grid-cols-2 gap-8">
              <a
                href="/foia-generator"
                className="bg-brand-dark rounded-lg p-8 space-y-4 border border-brand-orange/20 hover:border-brand-orange/50 transition-colors group"
              >
                <h3 className="font-agency text-h4 font-bold text-white group-hover:text-brand-orange transition-colors">
                  Generate Your Federal Request
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray">
                  Use our AI-powered FOIA Generator to create an optimized
                  request for federal records.
                </p>
                <div className="text-brand-azure font-martianmono font-bold">
                  Start Now →
                </div>
              </a>

              <a
                href="/wiki"
                className="bg-brand-dark rounded-lg p-8 space-y-4 border border-brand-orange/20 hover:border-brand-orange/50 transition-colors group"
              >
                <h3 className="font-agency text-h4 font-bold text-white group-hover:text-brand-orange transition-colors">
                  Back to Wiki
                </h3>
                <p className="font-martianmono text-text-md-custom text-brand-gray">
                  Explore state FOIA laws or learn more about public records
                  access.
                </p>
                <div className="text-brand-azure font-martianmono font-bold">
                  Explore →
                </div>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
